import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Mail, Send, Users, TrendingUp, Eye, MousePointerClick,
  ShoppingCart, Plus, ChevronLeft, ChevronRight, AlertCircle, CheckCircle
} from 'lucide-react';
import { fairManagerApi } from '../services/api';
import { extractApiError } from '../utils/errorUtils';
import type { EmailCampaignListResponse, EmailCampaignAnalytics, EmailCampaignCreate } from '../types';

/**
 * Fair Manager Email Campaigns Component
 * Issue #966 - Phase 1: Email Campaign Management
 *
 * Comprehensive email campaign management interface for Fair Managers.
 * Includes campaign creation, listing, and analytics in a single cohesive UI.
 */
export default function FairManagerEmailCampaigns() {
  const { fairId } = useParams<{ fairId: string }>();
  const navigate = useNavigate();

  // View state
  const [view, setView] = useState<'list' | 'create' | 'analytics'>('list');

  // List state
  const [campaigns, setCampaigns] = useState<EmailCampaignListResponse | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Create form state
  const [formData, setFormData] = useState<EmailCampaignCreate>({
    subject: '',
    message: '',
    target_type: 'all_vendors',
    target_filters: {},
    discount_code_id: undefined
  });
  const [creating, setCreating] = useState(false);
  const [createSuccess, setCreateSuccess] = useState(false);

  // Analytics state
  const [analytics, setAnalytics] = useState<EmailCampaignAnalytics | null>(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);

  useEffect(() => {
    if (view === 'list') {
      fetchCampaigns();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fairId, currentPage, view]);

  const fetchCampaigns = async () => {
    if (!fairId) return;

    try {
      setLoading(true);
      setError(null);
      const response = await fairManagerApi.listEmailCampaigns(parseInt(fairId), {
        page: currentPage,
        page_size: 10
      });
      setCampaigns(response);
    } catch (err: unknown) {
      const apiError = extractApiError(err);
      setError(apiError.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchAnalytics = async (campaignId: number) => {
    try {
      setAnalyticsLoading(true);
      setError(null);
      const response = await fairManagerApi.getEmailCampaignAnalytics(campaignId);
      setAnalytics(response);
    } catch (err: unknown) {
      const apiError = extractApiError(err);
      setError(apiError.message);
    } finally {
      setAnalyticsLoading(false);
    }
  };

  const handleCreateCampaign = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!fairId) return;

    try {
      setCreating(true);
      setError(null);
      await fairManagerApi.createEmailCampaign(parseInt(fairId), formData);
      setCreateSuccess(true);

      // Reset form and switch to list view after success
      setTimeout(() => {
        setFormData({
          subject: '',
          message: '',
          target_type: 'all_vendors',
          target_filters: {},
          discount_code_id: undefined
        });
        setCreateSuccess(false);
        setView('list');
      }, 2000);
    } catch (err: unknown) {
      const apiError = extractApiError(err);
      setError(apiError.message);
    } finally {
      setCreating(false);
    }
  };

  const handleViewAnalytics = (campaignId: number) => {
    setView('analytics');
    fetchAnalytics(campaignId);
  };

  // Render Create Campaign View
  const renderCreateView = () => (
    <div data-testid="campaign-form-modal" className="bg-white rounded-lg shadow-md p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Create Email Campaign</h2>
        <button
          onClick={() => setView('list')}
          className="text-secondary hover:text-ink flex items-center"
        >
          <ChevronLeft className="h-4 w-4 mr-1" />
          Back to Campaigns
        </button>
      </div>

      {createSuccess && (
        <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center">
            <CheckCircle className="h-5 w-5 text-green-600 mr-2" />
            <div>
              <p className="text-green-800 font-medium">Campaign Created Successfully!</p>
              <p className="text-green-700 text-sm mt-1">
                Your email campaign is being sent to recipients in the background.
              </p>
            </div>
          </div>
        </div>
      )}

      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <AlertCircle className="h-5 w-5 text-red-600 mr-2" />
            <p className="text-red-800">{error}</p>
          </div>
        </div>
      )}

      <form onSubmit={handleCreateCampaign} className="space-y-6">
        {/* Subject */}
        <div>
          <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-2">
            Email Subject *
          </label>
          <input
            type="text"
            id="subject"
            value={formData.subject}
            onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
            required
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-dusty-teal focus:border-transparent"
            placeholder="e.g., Join us at Summer Arts Festival 2026!"
            data-testid="campaign-subject-input"
          />
        </div>

        {/* Message */}
        <div>
          <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
            Email Message *
          </label>
          <textarea
            id="message"
            value={formData.message}
            onChange={(e) => setFormData({ ...formData, message: e.target.value })}
            required
            rows={8}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-dusty-teal focus:border-transparent"
            placeholder="Write your campaign message here..."
            data-testid="campaign-message-input"
          />
          <p className="text-sm text-gray-600 mt-1">
            Tip: Personalize your message with vendor names and fair details.
          </p>
        </div>

        {/* Target Type */}
        <div>
          <label htmlFor="target_type" className="block text-sm font-medium text-gray-700 mb-2">
            Target Audience *
          </label>
          <select
            id="target_type"
            value={formData.target_type}
            onChange={(e) => setFormData({
              ...formData,
              target_type: e.target.value as EmailCampaignCreate['target_type'],
              target_filters: {} // Reset filters when target type changes
            })}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-dusty-teal focus:border-transparent"
            data-testid="campaign-target-type-select"
          >
            <option value="all_vendors">All Active Vendors</option>
            <option value="product_types">Vendors by Product Type</option>
            <option value="geographic">Vendors Within Radius</option>
            <option value="custom_list">Custom Email List</option>
          </select>
        </div>

        {/* Target Filters (simplified for Phase 1) */}
        {formData.target_type === 'product_types' && (
          <div>
            <label htmlFor="product_types" className="block text-sm font-medium text-gray-700 mb-2">
              Product Types (comma-separated)
            </label>
            <input
              type="text"
              id="product_types"
              placeholder="e.g., jewelry, pottery, paintings"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-dusty-teal focus:border-transparent"
              onChange={(e) => setFormData({
                ...formData,
                target_filters: {
                  product_types: e.target.value.split(',').map(t => t.trim()).filter(t => t.length > 0)
                }
              })}
            />
          </div>
        )}

        {formData.target_type === 'geographic' && (
          <div>
            <label htmlFor="radius_miles" className="block text-sm font-medium text-gray-700 mb-2">
              Radius (miles)
            </label>
            <input
              type="number"
              id="radius_miles"
              min="1"
              max="500"
              placeholder="e.g., 50"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-dusty-teal focus:border-transparent"
              onChange={(e) => {
                const value = parseInt(e.target.value);
                setFormData({
                  ...formData,
                  target_filters: { radius_miles: isNaN(value) ? undefined : value }
                });
              }}
            />
          </div>
        )}

        {formData.target_type === 'custom_list' && (
          <div>
            <label htmlFor="custom_emails" className="block text-sm font-medium text-gray-700 mb-2">
              Vendor Emails (comma-separated)
            </label>
            <textarea
              id="custom_emails"
              rows={4}
              placeholder="vendor1@example.com, vendor2@example.com"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-dusty-teal focus:border-transparent"
              onChange={(e) => setFormData({
                ...formData,
                target_filters: {
                  custom_emails: e.target.value.split(',').map(t => t.trim()).filter(t => t.length > 0)
                }
              })}
            />
          </div>
        )}

        {/* Discount Code ID */}
        <div>
          <label htmlFor="discount_code_id" className="block text-sm font-medium text-gray-700 mb-2">
            Discount Code ID (optional)
          </label>
          <input
            type="number"
            id="discount_code_id"
            value={formData.discount_code_id || ''}
            onChange={(e) => setFormData({ ...formData, discount_code_id: e.target.value ? parseInt(e.target.value) : undefined })}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-dusty-teal focus:border-transparent"
            placeholder="e.g., 1"
            data-testid="campaign-discount-code-input"
          />
          <p className="text-sm text-gray-600 mt-1">
            Enter the ID of an existing discount code to include in the campaign.
          </p>
        </div>

        {/* Submit Button */}
        <div className="flex gap-4 pt-4 border-t border-gray-200">
          <button
            type="submit"
            disabled={creating || createSuccess}
            className="flex-1 bg-primary text-white px-6 py-3 rounded-lg hover:bg-primary-dark disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center transition-colors"
            data-testid="create-campaign-button"
          >
            {creating ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                Sending Campaign...
              </>
            ) : (
              <>
                <Send className="h-5 w-5 mr-2" />
                Create & Send Campaign
              </>
            )}
          </button>
          <button
            type="button"
            onClick={() => setView('list')}
            className="px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );

  // Render Analytics View
  const renderAnalyticsView = () => {
    if (analyticsLoading) {
      return (
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          </div>
        </div>
      );
    }

    if (!analytics) return null;

    return (
      <div data-testid="analytics-modal" className="bg-white rounded-lg shadow-md p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-gray-900">Campaign Analytics</h2>
          <button
            onClick={() => setView('list')}
            className="text-secondary hover:text-ink flex items-center"
          >
            <ChevronLeft className="h-4 w-4 mr-1" />
            Back to Campaigns
          </button>
        </div>

        {/* Metrics Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-moonbone rounded-lg p-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-medium text-secondary">Sent</p>
              <Mail className="h-5 w-5 text-secondary" />
            </div>
            <p className="text-3xl font-bold text-ink">{analytics.total_sent}</p>
          </div>

          <div className="bg-green-50 rounded-lg p-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-medium text-green-600">Opened</p>
              <Eye className="h-5 w-5 text-green-600" />
            </div>
            <p className="text-3xl font-bold text-green-900">{analytics.total_opened}</p>
            <p className="text-sm text-green-700 mt-1">{analytics.open_rate.toFixed(1)}% open rate</p>
          </div>

          <div className="bg-purple-50 rounded-lg p-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-medium text-purple-600">Clicked</p>
              <MousePointerClick className="h-5 w-5 text-purple-600" />
            </div>
            <p className="text-3xl font-bold text-purple-900">{analytics.total_clicked}</p>
            <p className="text-sm text-purple-700 mt-1">{analytics.click_rate.toFixed(1)}% click rate</p>
          </div>

          <div className="bg-orange-50 rounded-lg p-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-medium text-orange-600">Converted</p>
              <ShoppingCart className="h-5 w-5 text-orange-600" />
            </div>
            <p className="text-3xl font-bold text-orange-900">{analytics.total_converted}</p>
            <p className="text-sm text-orange-700 mt-1">{analytics.conversion_rate.toFixed(1)}% conversion</p>
          </div>
        </div>

        {/* Performance Summary */}
        <div className="border-t border-gray-200 pt-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Performance Summary</h3>
          <div className="space-y-3">
            <div className="flex items-center">
              <TrendingUp className="h-5 w-5 text-gray-400 mr-3" />
              <div className="flex-1">
                <div className="flex justify-between items-center mb-1">
                  <span className="text-sm text-gray-600">Open Rate</span>
                  <span className="text-sm font-medium text-gray-900">{analytics.open_rate.toFixed(1)}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-green-600 h-2 rounded-full transition-all"
                    style={{ width: `${Math.min(analytics.open_rate, 100)}%` }}
                  />
                </div>
              </div>
            </div>

            <div className="flex items-center">
              <TrendingUp className="h-5 w-5 text-gray-400 mr-3" />
              <div className="flex-1">
                <div className="flex justify-between items-center mb-1">
                  <span className="text-sm text-gray-600">Click Rate</span>
                  <span className="text-sm font-medium text-gray-900">{analytics.click_rate.toFixed(1)}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-purple-600 h-2 rounded-full transition-all"
                    style={{ width: `${Math.min(analytics.click_rate, 100)}%` }}
                  />
                </div>
              </div>
            </div>

            <div className="flex items-center">
              <TrendingUp className="h-5 w-5 text-gray-400 mr-3" />
              <div className="flex-1">
                <div className="flex justify-between items-center mb-1">
                  <span className="text-sm text-gray-600">Conversion Rate</span>
                  <span className="text-sm font-medium text-gray-900">{analytics.conversion_rate.toFixed(1)}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-orange-600 h-2 rounded-full transition-all"
                    style={{ width: `${Math.min(analytics.conversion_rate, 100)}%` }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Render List View
  const renderListView = () => (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Email Campaigns</h2>
        <button
          onClick={() => setView('create')}
          className="bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary-dark flex items-center transition-colors"
          data-testid="create-campaign-nav-button"
        >
          <Plus className="h-5 w-5 mr-2" />
          New Campaign
        </button>
      </div>

      {error && (
        <div data-testid="email-campaigns-error" className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <AlertCircle className="h-5 w-5 text-red-600 mr-2" />
            <p className="text-red-800">{error}</p>
          </div>
        </div>
      )}

      {loading ? (
        <div data-testid="email-campaigns-loading" className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      ) : campaigns && campaigns.items.length > 0 ? (
        <>
          <div className="space-y-4">
            {campaigns.items.map((campaign) => (
              <div
                key={campaign.id}
                className="border border-gray-200 rounded-lg p-4 hover:border-teal-300 transition-colors"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900 mb-1">{campaign.subject}</h3>
                    <p className="text-sm text-gray-600 mb-2 line-clamp-2">{campaign.message}</p>
                    <div className="flex items-center gap-4 text-sm text-gray-500">
                      <span className="flex items-center">
                        <Users className="h-4 w-4 mr-1" />
                        {campaign.total_recipients} recipients
                      </span>
                      <span className="flex items-center">
                        <Mail className="h-4 w-4 mr-1" />
                        {new Date(campaign.sent_at).toLocaleDateString()}
                      </span>
                      <span className="capitalize">{campaign.target_type.replace('_', ' ')}</span>
                    </div>
                  </div>
                  <button
                    onClick={() => handleViewAnalytics(campaign.id)}
                    className="ml-4 bg-moonbone text-secondary px-4 py-2 rounded-lg hover:bg-moonbone flex items-center transition-colors"
                    data-testid={`view-analytics-${campaign.id}`}
                  >
                    <TrendingUp className="h-4 w-4 mr-2" />
                    View Analytics
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Pagination */}
          {campaigns.total_pages > 1 && (
            <div data-testid="campaigns-pagination" className="flex items-center justify-between mt-6 pt-6 border-t border-gray-200">
              <p className="text-sm text-gray-600">
                Page {campaigns.page} of {campaigns.total_pages} ({campaigns.total} total campaigns)
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={campaigns.page === 1}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  Previous
                </button>
                <button
                  onClick={() => setCurrentPage(p => Math.min(campaigns.total_pages, p + 1))}
                  disabled={campaigns.page === campaigns.total_pages}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  Next
                  <ChevronRight className="h-4 w-4 ml-1" />
                </button>
              </div>
            </div>
          )}
        </>
      ) : (
        <div data-testid="email-campaigns-empty-state" className="text-center py-12">
          <Mail className="h-16 w-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-600 mb-4">No email campaigns yet</p>
          <button
            onClick={() => setView('create')}
            className="bg-primary text-white px-6 py-3 rounded-lg hover:bg-primary-dark inline-flex items-center transition-colors"
          >
            <Plus className="h-5 w-5 mr-2" />
            Create Your First Campaign
          </button>
        </div>
      )}
    </div>
  );

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="mb-6">
        <button
          onClick={() => navigate(`/fairmanager/fairs`)}
          className="text-secondary hover:text-ink flex items-center"
        >
          <ChevronLeft className="h-4 w-4 mr-1" />
          Back to Fairs
        </button>
      </div>

      {view === 'list' && renderListView()}
      {view === 'create' && renderCreateView()}
      {view === 'analytics' && renderAnalyticsView()}
    </div>
  );
}
